// @ts-nocheck -- Transitional typing, matching the Billing page this belongs to.
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, Clock, CreditCard, Info, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import SectionCard from "@/components/common/SectionCard";
import AddonPicker from "@/components/billing/AddonPicker";
import { usePreviewAddon, useUpdateAddon } from "@/hooks/useBilling";
import { addonsMonthlyTotal, changedQuantities } from "@/utils/addons.utils";
import { formatCurrency, formatUtcDate } from "@/utils/common.utils";
import { PUBLIC_ROUTES } from "@/routes/url";

/**
 * The add-ons card on the billing tab.
 *
 * Anchored at `#addons` because the plan-limit toast links straight here — the
 * whole point of add-ons is to be reachable at the moment someone is blocked.
 *
 * Changes are applied one add-on at a time. The endpoint takes a single
 * `{addon_id, quantity}` because Stripe's proration is computed per
 * subscription item, and batching two items into one request would mean
 * reporting one charge for two different prorations. In practice someone
 * adjusts one thing, so this is also the simpler UI.
 */
export default function AddonsCard({ billing, isOwner, onApplied, onCheckout, checkoutBusy }) {
  // Without a subscription there is nothing for an add-on item to
  // attach to. Stripe Checkout can't add items to a subscription that doesn't
  // exist, and a second add-on-only subscription would split them across two
  // invoices — so the path forward is to subscribe *with* the add-ons, which is
  // what `onCheckout` does: the same picker, carried into the Checkout Session.
  const canBuy = Boolean(billing?.is_subscribed);
  // A retired add-on — or one that raises nothing on this plan, like a deal
  // pack on Team/Growth, which have no deal cap — is listed only so a
  // subscriber still paying for it can step it down. Without a subscription
  // there is nothing to step down, and carrying it into a new checkout would
  // be buying it again.
  const addons = useMemo(
    () => (billing?.addons ?? []).filter(a => canBuy || (!a.retired && a.applies !== false)),
    [billing?.addons, canBuy]
  );
  const owned = useMemo(
    () => Object.fromEntries(addons.map(a => [a.id, a.quantity])),
    [addons]
  );
  const [draft, setDraft] = useState(owned);
  const [confirming, setConfirming] = useState(null);
  const { mutateAsync: updateAddon, isPending } = useUpdateAddon();

  const pendingAt = billing?.pending_addons_effective_at;

  // `draft` is seeded once. When a change lands, `billing` comes back with new
  // quantities and the two disagree — so re-seed off the server's answer rather
  // than trusting the local edit.
  const ownedKey = JSON.stringify(owned);
  const [seededFrom, setSeededFrom] = useState(ownedKey);
  if (seededFrom !== ownedKey) {
    setSeededFrom(ownedKey);
    setDraft(owned);
  }

  if (!billing?.addons_available || addons.length === 0) return null;

  const changes = changedQuantities(addons, draft);
  const draftTotal = addonsMonthlyTotal(addons, draft);
  const currentTotal = billing?.addons_monthly_total ?? 0;
  const monthlyDelta = Math.round((draftTotal - currentTotal) * 100) / 100;

  const apply = async change => {
    setConfirming(null);
    try {
      await updateAddon({
        addon_id: change.addon.id,
        quantity: change.quantity,
        origin_url: window.location.origin,
      });
      onApplied?.();
    } catch {
      // The hook and the axios interceptor have already said what went wrong;
      // reset to the server's truth so the steppers don't show a change that
      // was refused.
      setDraft(owned);
    }
  };

  return (
    <SectionCard
      id="addons"
      className="scroll-mt-24 rounded-2xl"
      title="Add-ons"
      description="Need more room than your plan includes? Add exactly what you need instead of moving up a tier."
      data-testid="billing-addons"
    >
      <AddonPicker
        addons={addons}
        value={draft}
        onChange={setDraft}
        owned={canBuy ? owned : undefined}
        disabled={!isOwner || isPending || checkoutBusy}
      />

      {pendingAt && (
        <div
          className="mt-4 flex items-start gap-2 rounded-xl bg-dash-amber/10 px-4 py-3 text-sm text-dash-ink"
          data-testid="addons-pending-notice"
        >
          <Clock className="mt-0.5 size-4 shrink-0 text-dash-amber" />
          <span>
            {addons
              .filter(a => a.pending_quantity != null && a.pending_quantity !== a.quantity)
              .map(a => `${a.name} drops to ${a.pending_quantity}`)
              .join(", ")}{" "}
            on {formatUtcDate(pendingAt)}. You keep what you&apos;ve paid for until then.
          </span>
        </div>
      )}

      {!canBuy && (
        <div
          className="mt-4 flex items-start gap-2 rounded-xl bg-dash-hover px-4 py-3 text-sm text-dash-muted"
          data-testid="addons-needs-subscription"
        >
          <Info className="mt-0.5 size-4 shrink-0" />
          <span>
            Add-ons are billed with your plan, on one invoice. Pick what you need
            here and they&apos;ll be in the same checkout — or{" "}
            <Link to={PUBLIC_ROUTES.PRICING} className="font-medium text-dash-greenDeep hover:underline">
              compare plans first
            </Link>
            .
          </span>
        </div>
      )}

      <div className="mt-5 flex flex-col gap-3 border-t border-dash-border pt-5 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-dash-muted">
          Add-ons total{" "}
          <span className="font-semibold text-dash-ink" data-testid="addons-total">
            {formatCurrency(draftTotal)}/month
          </span>
          {monthlyDelta !== 0 && (
            <span className={monthlyDelta > 0 ? "text-dash-ink" : "text-dash-greenDeep"}>
              {" "}
              ({monthlyDelta > 0 ? "+" : "−"}
              {formatCurrency(Math.abs(monthlyDelta))}/mo)
            </span>
          )}
        </p>
        <div className="flex gap-2">
          {canBuy && changes.length > 0 && (
            <Button
              type="button"
              variant="dashboardOutline"
              onClick={() => setDraft(owned)}
              disabled={isPending}
              data-testid="addons-reset"
            >
              Cancel
            </Button>
          )}
          {canBuy ? (
            <Button
              type="button"
              variant="dashboardPrimary"
              onClick={() => setConfirming(changes[0])}
              disabled={!isOwner || isPending || changes.length === 0}
              loading={isPending}
              data-testid="addons-save"
            >
              {changes.length > 1 ? `Update ${changes[0]?.addon.name}` : "Update add-ons"}
            </Button>
          ) : (
            <Button
              type="button"
              variant="dashboardPrimary"
              onClick={() => onCheckout?.(draft)}
              disabled={!isOwner || draftTotal === 0 || checkoutBusy}
              loading={checkoutBusy}
              data-testid="addons-checkout"
            >
              Subscribe with these add-ons
            </Button>
          )}
        </div>
      </div>

      {canBuy && changes.length > 1 && (
        <p className="mt-2 text-right text-xs text-dash-faint">
          One add-on at a time — each is billed separately.
        </p>
      )}

      {!isOwner && (
        <p className="mt-3 text-center text-xs text-dash-faint">
          Only owners and admins can manage billing.
        </p>
      )}

      {confirming && (
        <ConfirmChange
          change={confirming}
          paymentMethod={billing?.payment_method}
          onCancel={() => setConfirming(null)}
          onConfirm={() => apply(confirming)}
          busy={isPending}
        />
      )}
    </SectionCard>
  );
}

/**
 * Names what is about to happen, in money, before it happens.
 *
 * The first version said "you'll be charged the prorated difference" and left
 * it at that. That is not a claim anyone can check: adding one $10 deal
 * nineteen days into a thirty-day period charges $6.44, which is correct and
 * reads exactly like a bug. So the amount is fetched from Stripe's invoice
 * preview and shown, along with the arithmetic behind it and the card it will
 * land on — there is no Stripe Checkout page in this flow, because Checkout
 * cannot add items to a subscription that already exists, so this dialog is
 * the only chance to say what is about to happen.
 *
 * The preview is advisory. If it fails the dialog still opens and still buys,
 * with the unquoted wording — a number we could not fetch must not become a
 * reason someone cannot pay us.
 */
function ConfirmChange({ change, paymentMethod, onCancel, onConfirm, busy }) {
  const { addon, quantity } = change;
  const increasing = quantity > addon.quantity;
  const unit = quantity === 1 ? addon.unit_label : addon.unit_label_plural;
  const { mutateAsync: preview, isPending: previewing } = usePreviewAddon();
  const [quote, setQuote] = useState(null);

  useEffect(() => {
    let live = true;
    preview({ addon_id: addon.id, quantity })
      .then(res => { if (live) setQuote(res.data ?? null); })
      .catch(() => { if (live) setQuote(null); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addon.id, quantity]);

  const dueToday = quote?.previewable ? quote.amount_due : null;
  const card = paymentMethod
    ? `${paymentMethod.brand} ending ${paymentMethod.last4}`
    : "your card on file";

  return (
    <div
      className="mt-5 rounded-xl border border-dash-border bg-dash-searchBg p-4"
      data-testid="addons-confirm"
    >
      <div className="flex items-start gap-2.5">
        <AlertCircle className="mt-0.5 size-4 shrink-0 text-dash-greenDeep" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-dash-ink">
            {increasing
              ? `Add ${quantity - addon.quantity} more — ${quantity} extra ${unit} in total?`
              : quantity === 0
                ? `Remove ${addon.name.toLowerCase()}?`
                : `Reduce to ${quantity} extra ${unit}?`}
          </p>

          {increasing ? (
            <div className="mt-3 space-y-3">
              <div className="rounded-lg border border-dash-border bg-white p-3" data-testid="addons-confirm-quote">
                {previewing ? (
                  <p className="flex items-center gap-2 text-sm text-dash-muted">
                    <Loader2 className="size-3.5 animate-spin" /> Working out the exact amount…
                  </p>
                ) : dueToday != null ? (
                  <>
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="text-sm text-dash-muted">Charged today</span>
                      <span className="text-lg font-bold text-dash-ink" data-testid="addons-due-today">
                        {formatCurrency(dueToday / 100, { maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    {/* The line items are the answer to "why isn't this the
                        sticker price?" — they show the credit for time already
                        paid for and the charge for the days remaining. */}
                    {(quote?.lines ?? []).length > 0 && (
                      <ul className="mt-2 space-y-1 border-t border-dash-border pt-2">
                        {quote.lines.map((line, i) => (
                          <li key={i} className="flex items-baseline justify-between gap-3 text-xs text-dash-faint">
                            <span className="min-w-0 truncate">{line.description}</span>
                            <span className="shrink-0 tabular-nums">
                              {formatCurrency(line.amount / 100, { maximumFractionDigits: 2 })}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                    <p className="mt-2 border-t border-dash-border pt-2 text-xs text-dash-muted">
                      Only the days left in this billing period
                      {quote?.next_invoice_at ? ` (to ${formatUtcDate(quote.next_invoice_at)})` : ""}. From
                      your next invoice this line is{" "}
                      <span className="font-medium text-dash-ink">
                        {formatCurrency(quote?.new_monthly_total ?? addon.amount * quantity)}/month
                      </span>
                      .
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-dash-muted">
                    You&apos;ll be charged the prorated difference for the days left in this
                    billing period. From your next invoice this line is{" "}
                    {formatCurrency(addon.amount * quantity)}/month.
                  </p>
                )}
              </div>
              <p className="flex items-center gap-2 text-xs text-dash-faint" data-testid="addons-confirm-card">
                <CreditCard className="size-3.5 shrink-0" />
                Charged to {card} now — there&apos;s no further payment step. Your new
                limit is live immediately.
              </p>
            </div>
          ) : (
            <p className="mt-1 text-sm leading-6 text-dash-muted">
              You keep the capacity you&apos;ve already paid for until your next
              renewal{quote?.effective_at ? ` on ${formatUtcDate(quote.effective_at)}` : ""}, then this
              line drops to {formatCurrency(addon.amount * quantity)}/month.{" "}
              <span className="font-medium text-dash-ink">Nothing is charged or refunded today.</span>
            </p>
          )}

          <div className="mt-4 flex gap-2">
            <Button type="button" variant="dashboardPrimary" onClick={onConfirm} loading={busy} disabled={previewing} data-testid="addons-confirm-yes">
              {increasing
                ? dueToday != null
                  ? `Pay ${formatCurrency(dueToday / 100, { maximumFractionDigits: 2 })}`
                  : "Confirm and pay"
                : "Confirm change"}
            </Button>
            <Button type="button" variant="dashboardOutline" onClick={onCancel} disabled={busy} data-testid="addons-confirm-no">
              Back
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
